import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { aiApi, accountApi, productApi } from '../../modules/api';
import { openAuthView } from '../../modules/auth';
import {
  getCollectionsForUser,
  subscribeToCollectionChanges,
} from '../../modules/collectionStorage';
import {
  ensureSubscription,
  getSubscription,
  getPendingRequestForUser,
  subscribeToRequestChanges,
  subscribeToSubscriptionChanges,
  setSubscription as persistSubscription,
} from '../../modules/subscriptionStorage';
import PLAN_CATALOG, { getPlanById } from '../../constants/plans';
import {
  getPublishedPlans,
  subscribeToPublishedPlans,
} from '../../modules/publicPlanStorage';
import { setSelectedPlanId } from '../../modules/planSelectionStorage';
import {
  formatMonthlyPrice,
} from '../../utils/pricing';
import { mapSubscriptionFromApi } from '../../utils/subscription';
import LANDING_PLAN_FALLBACK from '../../constants/landingPlans';
import heroCollage from '../../assets/landing/hero-collage.png';
import whaleImage from '../../assets/landing/whale.png';

import './home.css';

const CURRENT_USER_ID = 'user-001';
const FALLBACK_PLAN_ID = 'basic-pack';
const MAX_HISTORY_ITEMS = 6;

const FALLBACK_PUBLISHED_PLANS = PLAN_CATALOG.map((plan) => ({
  planId: plan.id,
  productId: plan.id,
  name: plan.name,
  priceAmount: plan.priceAmount,
  priceDisplay: plan.priceDisplay,
  billingPeriod: plan.billingPeriod,
  billingUnit: plan.billingUnit,
  features: Array.isArray(plan.features) ? [...plan.features] : [],
}));

const LEGACY_PLAN_ID_MAP = {};

const safeArray = (value) => (Array.isArray(value) ? value : []);

const FALLBACK_HISTORY = [
  {
    id: 'activity-1',
    prompt:
      'Futuristic neon workspace, soft rim lighting, cinematic composition, depth of field',
    createdAt: '2024-06-12T12:00:00Z',
    status: 'completed',
    imageUrl:
      'https://images.unsplash.com/photo-1526498460520-4c246339dccb?auto=format&fit=crop&w=600&q=80',
  },
  {
    id: 'activity-2',
    prompt: 'Moody portrait of a fashion designer in studio, medium format, muted palette',
    createdAt: '2024-06-11T08:30:00Z',
    status: 'saved',
    imageUrl:
      'https://images.unsplash.com/photo-1542202229-7d93c33f5d07?auto=format&fit=crop&w=600&q=80',
  },
  {
    id: 'activity-3',
    prompt:
      'Sequence of panels showing sunrise over desert metropolis, painterly brushwork',
    createdAt: '2024-06-10T16:45:00Z',
    status: 'shared',
    imageUrl:
      'https://images.unsplash.com/photo-1526481280695-3c46917facc4?auto=format&fit=crop&w=600&q=80',
  },
];

function createFallbackSubscription() {
  const catalogPlan = getPlanById(FALLBACK_PLAN_ID);
  const publishedFallback =
    FALLBACK_PUBLISHED_PLANS.find((plan) => plan.planId === FALLBACK_PLAN_ID) ||
    FALLBACK_PUBLISHED_PLANS[0] ||
    null;

  const sourcePlan = publishedFallback || catalogPlan;
  if (!sourcePlan) {
    return null;
  }

  return {
    planId: sourcePlan.planId || sourcePlan.id || FALLBACK_PLAN_ID,
    planName: sourcePlan.name || 'Monthly Credit Plan',
    planFeatures: safeArray(sourcePlan.features),
    planPriceDisplay: sourcePlan.priceDisplay || null,
    planPriceAmount:
      sourcePlan.priceAmount !== undefined
        ? sourcePlan.priceAmount
        : (() => {
            if (sourcePlan.price != null) {
              const numeric = Number(String(sourcePlan.price).replace(/[^0-9.]/g, ''));
              return Number.isFinite(numeric) ? numeric : null;
            }
            if (sourcePlan.priceDisplay) {
              const numeric = Number(String(sourcePlan.priceDisplay).replace(/[^0-9.]/g, ''));
              return Number.isFinite(numeric) ? numeric : null;
            }
            return null;
          })(),
    planBillingPeriod:
      sourcePlan.billingPeriod !== undefined ? sourcePlan.billingPeriod : sourcePlan.period || 1,
    planBillingUnit: sourcePlan.billingUnit || sourcePlan.periodUnit || 'month',
    userName: '',
    userEmail: '',
  };
}

const FALLBACK_SUBSCRIPTION = createFallbackSubscription();

const renderLandingBrand = () => (
  <>
    SO<span className="home-landing__brand-accent">A</span>L
    <span className="home-landing__brand-accent">I</span>VE
  </>
);

const mapUserToProfile = (user) => ({
  displayName: (user && (user.name || user.username)) || '',
  email: (user && user.email) || '',
});

const splitLandingPriceText = (value) => {
  const text = String(value || '').trim();
  if (!text) {
    return { price: '', priceSuffix: '' };
  }
  const match = text.match(/^(.*?)(\s*\/\s*month)$/i);
  if (!match) {
    return { price: text, priceSuffix: '' };
  }
  return {
    price: match[1].trim(),
    priceSuffix: '/ month',
  };
};

const normalizeLandingPlans = (plans) =>
  (Array.isArray(plans) ? plans : [])
    .filter(Boolean)
    .sort(
      (left, right) =>
        (Number(left.displayOrder) || 0) - (Number(right.displayOrder) || 0),
    )
    .map((plan, index) => {
      const catalogPlan =
        PLAN_CATALOG.find(
          (entry) =>
            entry.id === plan.planId ||
            entry.name === plan.title ||
            entry.productDescription === plan.title,
        ) || null;
      const { price, priceSuffix } = splitLandingPriceText(plan.priceText);
      return {
        id:
          (catalogPlan && catalogPlan.id) ||
          plan.planId ||
          plan.productId ||
          plan.id ||
          `landing-plan-${index + 1}`,
        title: plan.title || 'Plan',
        tagline: plan.tagline || '',
        description: plan.description || '',
        price,
        priceSuffix,
        billing: plan.billingText || '',
        saveText: plan.saveText || '',
        buttonLabel: plan.buttonLabel || 'Select Plan',
        detailsLabel: 'What you get',
        bestFor: plan.bestFor || '',
        badge: plan.badge || '',
        theme: plan.theme || 'lilac',
        creditsPerYear: Number(plan.creditsPerYear) || 0,
        bonusCredits: Number(plan.bonusCredits) || 0,
        totalCredits: Number(plan.totalCredits) || Number(plan.generationLimit) || 0,
        features: Array.isArray(plan.features) ? plan.features : [],
      };
    });

const selectRecommendedPlan = (plans) => {
  const planSource =
    (Array.isArray(plans) && plans.length && plans[0]) ||
    FALLBACK_PUBLISHED_PLANS[0] ||
    null;
  if (!planSource && !FALLBACK_SUBSCRIPTION) {
    return null;
  }
  if (!planSource) {
    return { ...FALLBACK_SUBSCRIPTION, status: 'recommended' };
  }

  const planId = planSource.planId || planSource.productId || planSource.id || FALLBACK_PLAN_ID;
  const priceAmountCandidates = [
    planSource.priceAmount,
    planSource.planPriceAmount,
    planSource.price,
  ];
  const priceAmount =
    priceAmountCandidates
      .map((candidate) => {
        if (candidate == null) {
          return null;
        }
        const numeric =
          typeof candidate === 'number'
            ? candidate
            : Number(String(candidate).replace(/[^0-9.]/g, ''));
        return Number.isFinite(numeric) ? numeric : null;
      })
      .find((value) => value != null) || 0;
  const billingPeriod =
    planSource.billingPeriod != null
      ? planSource.billingPeriod
      : planSource.period != null
      ? planSource.period
      : 1;
  const billingUnit = planSource.billingUnit || planSource.periodUnit || 'month';

  return {
    planId,
    planName: planSource.name || planSource.description || 'Credit Plan',
    planPriceAmount: priceAmount,
    planBillingPeriod: billingPeriod,
    planBillingUnit: billingUnit,
    planFeatures: Array.isArray(planSource.features) ? planSource.features : [],
    planPriceDisplay: formatMonthlyPrice(priceAmount, billingPeriod, billingUnit),
    status: 'recommended',
  };
};

const toTimestamp = (value) => {
  if (!value) {
    return 0;
  }
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
};

const truncate = (text, max) => {
  if (!text || typeof text !== 'string') {
    return '';
  }
  if (text.length <= max) {
    return text;
  }
  return `${text.slice(0, max - 1).trim()}…`;
};

const normalizeBillingUnit = (unit) => {
  const normalized = String(unit || 'month').trim().toLowerCase();
  if (normalized.startsWith('year')) {
    return 'year';
  }
  if (normalized.startsWith('month')) {
    return 'month';
  }
  if (normalized.startsWith('week')) {
    return 'week';
  }
  if (normalized.startsWith('day')) {
    return 'day';
  }
  return normalized || 'month';
};

const formatRenewalLabel = (periodInput, unitInput) => {
  const numeric = Number(periodInput);
  const period = Number.isFinite(numeric) && numeric > 0 ? numeric : 1;
  const unit = normalizeBillingUnit(unitInput);
  const baseLabelMap = {
    year: 'Year',
    month: 'Month',
    week: 'Week',
    day: 'Day',
  };
  const baseLabel = baseLabelMap[unit] || 'Cycle';
  if (period === 1) {
    return `Renewed Every ${baseLabel}`;
  }
  const pluralLabel =
    baseLabel === 'Cycle' ? 'Cycles' : `${baseLabel}${baseLabel.endsWith('s') ? 'es' : 's'}`;
  return `Renewed Every ${period} ${pluralLabel}`;
};

const toNumericAmount = (value) => {
  if (value == null) {
    return null;
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const numeric = Number(String(value).replace(/[^0-9.]/g, ''));
  return Number.isFinite(numeric) ? numeric : null;
};

const resolveMonthlyPriceLabel = ({ plan, subscription }) => {
  const amountCandidates = [
    plan && plan.priceAmount,
    subscription && subscription.planPriceAmount,
    subscription && subscription.priceAmount,
    subscription && subscription.planPriceDisplay,
    plan && plan.price,
  ]
    .map(toNumericAmount)
    .filter((candidate) => candidate != null);

  const resolvedAmount = amountCandidates.length > 0 ? amountCandidates[0] : 0;
  const periodCandidates = [
    plan && plan.billingPeriod,
    subscription && subscription.planBillingPeriod,
    subscription && subscription.billingPeriod,
  ]
    .map((value) => {
      const numeric = Number(value);
      return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
    })
    .filter((candidate) => candidate != null);

  const resolvedPeriod = periodCandidates.length > 0 ? periodCandidates[0] : 1;
  const resolvedUnit =
    (plan && plan.billingUnit) ||
    (subscription && (subscription.planBillingUnit || subscription.billingUnit)) ||
    'month';

  return formatMonthlyPrice(resolvedAmount, resolvedPeriod, resolvedUnit);
};

const SUBSCRIPTION_STATUS_LABELS = {
  approved: 'Active',
  pending: 'Pending approval',
  cancelled: 'Cancelled',
  declined: 'Declined',
};

const formatRelativeTime = (isoString) => {
  if (!isoString) {
    return 'Just now';
  }
  try {
    const target = new Date(isoString);
    if (Number.isNaN(target.getTime())) {
      return 'Just now';
    }
    const now = Date.now();
    const diffMs = now - target.getTime();
    const absMs = Math.abs(diffMs);
    const minute = 60 * 1000;
    const hour = 60 * minute;
    const day = 24 * hour;
    const week = 7 * day;
    const format = (value, unit) => `${value} ${unit}${value === 1 ? '' : 's'} ${
      diffMs >= 0 ? 'ago' : 'from now'
    }`;

    if (absMs < minute) {
      return 'Moments ago';
    }
    if (absMs < hour) {
      const value = Math.round(absMs / minute);
      return format(value, 'min');
    }
    if (absMs < day) {
      const value = Math.round(absMs / hour);
      return format(value, 'hour');
    }
    if (absMs < week) {
      const value = Math.round(absMs / day);
      return format(value, 'day');
    }
    const value = Math.round(absMs / week);
    return format(value, 'week');
  } catch (error) {
    return 'Just now';
  }
};

const formatDateShort = (isoString) => {
  if (!isoString) {
    return 'Updated recently';
  }
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) {
      return 'Updated recently';
    }
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (error) {
    return 'Updated recently';
  }
};

const normalizeHistoryEntry = (entry, index) => {
  if (!entry) {
    return null;
  }
  const fallbackImage = `https://picsum.photos/seed/history-${index + 1}/640/400`;
  const rawImage = entry.image || entry.previewImage || entry.preview || entry.cover;
  const imageUrl =
    (rawImage && typeof rawImage === 'string' && rawImage) ||
    (rawImage && typeof rawImage === 'object' && (rawImage.url || rawImage.src)) ||
    fallbackImage;

  return {
    id: String(entry.id || entry._id || `history-${index + 1}`),
    prompt: entry.prompt || 'Untitled prompt',
    createdAt:
      entry.createdAt && !Number.isNaN(new Date(entry.createdAt).getTime())
        ? entry.createdAt
        : new Date().toISOString(),
    status: entry.status || 'completed',
    imageUrl,
  };
};

const normalizeCollection = (collection, index) => {
  if (!collection) {
    return null;
  }

  const artworks = safeArray(collection.artworks);
  const coverImage =
    (collection.coverImage &&
      ((typeof collection.coverImage === 'string' && collection.coverImage) ||
        (typeof collection.coverImage === 'object' &&
          (collection.coverImage.url || collection.coverImage.src)))) ||
    (artworks[0] && (artworks[0].url || artworks[0].src)) ||
    `https://picsum.photos/seed/collection-${index + 1}/720/480`;

  return {
    id: String(collection.id || `collection-${index + 1}`),
    title: collection.title || 'Untitled collection',
    description: collection.description || '',
    curator: collection.curator || 'Workspace collection',
    updatedAt: collection.updatedAt || collection.createdAt || null,
    artworkCount: artworks.length,
    tags: safeArray(collection.tags).slice(0, 3),
    coverImage,
  };
};

const mapCollections = (collections) =>
  safeArray(collections)
    .map(normalizeCollection)
    .filter(Boolean);

const sortCollections = (collections) =>
  [...collections].sort((a, b) => toTimestamp(b.updatedAt) - toTimestamp(a.updatedAt));

const LandingHome = ({ history, openAuthView: showAuthView, user }) => {
  const routerHistory = history || { push: () => {} };
  const isAuthenticated = Boolean(user);
  const [memberProfile, setMemberProfile] = useState(() => mapUserToProfile(user));
  const [accountSubscription, setAccountSubscription] = useState(null);
  const [isAccountSubscriptionLoading, setIsAccountSubscriptionLoading] = useState(false);
  const memberName = memberProfile.displayName || memberProfile.email || 'Member';
  const [landingPlanCards, setLandingPlanCards] = useState(() =>
    normalizeLandingPlans(LANDING_PLAN_FALLBACK),
  );
  const planRailRef = useRef(null);

  const scrollPlanRail = useCallback((direction) => {
    const rail = planRailRef.current;
    if (!rail) {
      return;
    }

    const amount = Math.max(Math.round(rail.clientWidth * 0.55), 240);
    rail.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    setMemberProfile((prev) => ({
      ...prev,
      ...mapUserToProfile(user),
    }));
  }, [user]);

  useEffect(() => {
    let isSubscribed = true;

    productApi.fetchPublicProducts()
      .then((response) => {
        if (!isSubscribed) {
          return;
        }
        const data = response && response.data ? response.data : response;
        const normalized = normalizeLandingPlans(data);
        if (normalized.length > 0) {
          setLandingPlanCards(normalized);
        }
      })
      .catch(() => {});

    return () => {
      isSubscribed = false;
    };
  }, []);

  useLayoutEffect(() => {
    const rail = planRailRef.current;
    if (!rail) {
      return undefined;
    }

    const updateInitialScroll = () => {
      if (window.innerWidth <= 900) {
        rail.scrollLeft = 0;
        return;
      }

      const cards = rail.querySelectorAll('.home-landing__plan-card-shell');
      if (!cards || cards.length <= 4) {
        rail.scrollLeft = 0;
        return;
      }

      const targetCard = cards[Math.max(cards.length - 4, 0)];
      if (!targetCard) {
        return;
      }

      rail.scrollLeft = Math.max(targetCard.offsetLeft - 12, 0);
    };

    updateInitialScroll();
    window.addEventListener('resize', updateInitialScroll);
    return () => window.removeEventListener('resize', updateInitialScroll);
  }, [landingPlanCards]);

  useEffect(() => {
    let isMounted = true;

    async function loadAccountSummary() {
      if (!isAuthenticated || !user || !user.id) {
        if (isMounted) {
          setAccountSubscription(null);
          setIsAccountSubscriptionLoading(false);
        }
        return;
      }

      setIsAccountSubscriptionLoading(true);
      try {
        const response = await accountApi.fetchProfile();
        if (!isMounted) {
          return;
        }
        const payload = response && response.data ? response.data : {};
        if (payload.user) {
          setMemberProfile((prev) => ({
            ...prev,
            ...mapUserToProfile(payload.user),
          }));
        }
        setAccountSubscription(
          payload.subscription ? mapSubscriptionFromApi(payload.subscription) : null,
        );
      } catch (error) {
        if (isMounted) {
          setAccountSubscription(null);
        }
      } finally {
        if (isMounted) {
          setIsAccountSubscriptionLoading(false);
        }
      }
    }

    loadAccountSummary();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, user]);

  const handlePrimaryEntry = useCallback(() => {
    if (isAuthenticated) {
      routerHistory.push('/ai');
      return;
    }
    showAuthView('login');
  }, [isAuthenticated, routerHistory, showAuthView]);

  const handleSecondaryEntry = useCallback(() => {
    if (isAuthenticated) {
      routerHistory.push('/account');
      return;
    }
    showAuthView('register');
  }, [isAuthenticated, routerHistory, showAuthView]);

  const handlePlanAction = useCallback((planId) => {
    if (isAuthenticated) {
      setSelectedPlanId(planId);
      routerHistory.push('/account');
      return;
    }
    showAuthView('login');
  }, [isAuthenticated, routerHistory, showAuthView]);

  const activePlanName =
    accountSubscription && accountSubscription.planName ? accountSubscription.planName : null;
  const activePlanBilling =
    accountSubscription && accountSubscription.planPriceDisplay
      ? accountSubscription.planPriceDisplay
      : null;
  const activePlanQuotaLimit =
    accountSubscription && Number.isFinite(accountSubscription.quotaLimit)
      ? accountSubscription.quotaLimit
      : null;
  const activePlanQuotaUsed =
    accountSubscription && Number.isFinite(accountSubscription.quotaUsed)
      ? accountSubscription.quotaUsed
      : 0;
  const activePlanQuotaRemaining =
    accountSubscription && accountSubscription.quotaRemaining != null
      ? Number(accountSubscription.quotaRemaining)
      : activePlanQuotaLimit != null
      ? Math.max(activePlanQuotaLimit - activePlanQuotaUsed, 0)
      : null;
  const isQuotaExhausted = Boolean(accountSubscription && accountSubscription.isQuotaExhausted);

  return (
    <div className="home-landing">
      <section className="home-landing__hero">
        {!isAuthenticated ? (
          <div className="home-landing__topbar">
            <div className="home-landing__topbar-brand">{renderLandingBrand()}</div>
            <div className="home-landing__topbar-actions">
              <button
                type="button"
                className="home-landing__topbar-cta"
                onClick={handlePrimaryEntry}
              >
                <span className="home-landing__topbar-cta-desktop">Try SOALIVE for free</span>
                <span className="home-landing__topbar-cta-mobile">Try SOALIVE</span>
              </button>
              <button
                type="button"
                className="home-landing__topbar-link"
                onClick={handleSecondaryEntry}
              >
                Log in
              </button>
            </div>
          </div>
        ) : null}
        <div className="home-landing__hero-copy">
          {isAuthenticated ? (
            <p className="home-landing__member-kicker">Welcome back, {memberName}</p>
          ) : null}
          <h1>AI that brings ideas to life</h1>
          {isAuthenticated ? (
            <p className="home-landing__member-copy">
              Pick up where you left off, jump into image generation, and keep your workspace moving.
            </p>
          ) : null}
        </div>
        <div className="home-landing__hero-media">
          <img src={heroCollage} alt="SOALIVE creative collage" />
        </div>
        <div className="home-landing__hero-action">
          <button
            type="button"
            className="home-landing__primary-btn"
            onClick={handlePrimaryEntry}
          >
            Generate Now
          </button>
        </div>
      </section>

      {isAuthenticated ? (
        <section className="home-landing__member-strip" aria-label="Workspace shortcuts">
          <div className="home-landing__member-grid">
            <button
              type="button"
              className="home-landing__member-card home-landing__member-card--plan"
              onClick={() => routerHistory.push('/account')}
            >
              <span className="home-landing__member-card-label">Active plan</span>
              <strong>
                {isAccountSubscriptionLoading
                  ? 'Loading plan...'
                  : activePlanName || 'No active plan'}
              </strong>
              <p>
                {isAccountSubscriptionLoading
                  ? 'Fetching your billing and generation details.'
                  : activePlanBilling
                  ? `${activePlanBilling}${activePlanQuotaLimit != null ? ` • ${activePlanQuotaRemaining.toLocaleString()} of ${activePlanQuotaLimit.toLocaleString()} generations left` : ''}`
                  : 'Open Account to review your current credit plan.'}
              </p>
              {accountSubscription && accountSubscription.approvedAt ? (
                <span className={`home-landing__member-card-status${isQuotaExhausted ? ' is-danger' : ''}`}>
                  {isQuotaExhausted ? 'Quota exhausted' : 'Credit Plan active'}
                </span>
              ) : null}
            </button>
            <button
              type="button"
              className="home-landing__member-card"
              onClick={() => routerHistory.push('/ai')}
            >
              <span className="home-landing__member-card-label">AI Studio</span>
              <strong>Create new visuals</strong>
              <p>Start a prompt, generate variants, and download ready-to-use outputs.</p>
            </button>
            <button
              type="button"
              className="home-landing__member-card"
              onClick={() => routerHistory.push('/templates')}
            >
              <span className="home-landing__member-card-label">Templates</span>
              <strong>Review saved work</strong>
              <p>Open collections, revisit inspiration, and keep the strongest outputs organized.</p>
            </button>
          </div>
        </section>
      ) : null}

      <section className="home-landing__plans">
        <div className="home-landing__plans-header">
          <h2>Wake Up Your Plan</h2>
          <p>Create more. Create faster. Create without limits.</p>
          <p>Unlimited generations and the newest features, all in one plan.</p>
        </div>
        <div className="home-landing__plan-rail">
          <button
            type="button"
            className="home-landing__plan-rail-control home-landing__plan-rail-control--left"
            aria-label="Show previous plans"
            onClick={() => scrollPlanRail('left')}
          >
            ‹
          </button>
          <div className="home-landing__plan-grid" ref={planRailRef}>
          {landingPlanCards.map((plan) => (
            <div
              key={plan.id}
              className={[
                'home-landing__plan-card-shell',
                `home-landing__plan-card-shell--${plan.theme}`,
                plan.title === 'Infinity Pack' ? 'home-landing__plan-card-shell--ultimate' : '',
              ].filter(Boolean).join(' ')}
            >
              {plan.badge ? <span className="home-landing__plan-badge">{plan.badge}</span> : null}
              <article className={`home-landing__plan-card home-landing__plan-card--${plan.theme}`}>
                <div className="home-landing__plan-intro">
                  <h3>{plan.title}</h3>
                  <p className="home-landing__plan-tagline">{plan.tagline}</p>
                  <p className="home-landing__plan-description">{plan.description}</p>
                  <div className="home-landing__plan-price">
                    <strong>{plan.price}</strong>
                    {plan.priceSuffix ? <span>{plan.priceSuffix}</span> : null}
                  </div>
                  <div className="home-landing__plan-pricing-note">
                    <p className="home-landing__plan-billing">{plan.billing}</p>
                    {plan.saveText ? <p className="home-landing__plan-save">{plan.saveText}</p> : null}
                  </div>
                  <div className="home-landing__plan-credit-summary">
                    <div className="home-landing__plan-credit-total">
                      <span>Total credits</span>
                      <strong>{plan.totalCredits.toLocaleString('en-US')}</strong>
                    </div>
                    <div className="home-landing__plan-credit-breakdown">
                      <span>{plan.creditsPerYear.toLocaleString('en-US')} yearly</span>
                      <span>{plan.bonusCredits.toLocaleString('en-US')} bonus</span>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className={[
                    'home-landing__plan-btn',
                    plan.title === 'Infinity Pack' ? 'home-landing__plan-btn--ultimate' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => handlePlanAction(plan.id)}
                >
                  {isAuthenticated ? 'View Plan' : plan.buttonLabel || 'Select Plan'}
                </button>
                <p className="home-landing__plan-detail-label">{plan.detailsLabel || 'What you get'}</p>
                <ul className="home-landing__plan-features">
                  {plan.features.map((feature) => (
                    <li key={`${plan.id}-${feature}`}>{feature}</li>
                  ))}
                </ul>
                <p className="home-landing__plan-best-for">
                  Best for:
                  <span>{plan.bestFor}</span>
                </p>
              </article>
            </div>
          ))}
          </div>
          <button
            type="button"
            className="home-landing__plan-rail-control home-landing__plan-rail-control--right"
            aria-label="Show more plans"
            onClick={() => scrollPlanRail('right')}
          >
            ›
          </button>
        </div>
        <div className="home-landing__plans-footer">
          <p>Wake Up Your Plan is built for people who don&apos;t want to stay stuck.</p>
          <p>Pick the plan that matches your vibe and start moving.</p>
          <button
            type="button"
            className="home-landing__enterprise-btn"
            onClick={handleSecondaryEntry}
          >
            {isAuthenticated ? 'Manage Credit Plan' : 'Enterprise Plan'}
          </button>
        </div>
      </section>

      <section className="home-landing__cta">
        <h2>
          Make your own Idea <span className="home-landing__cta-brand">{renderLandingBrand()}</span>.
        </h2>
        <button
          type="button"
          className="home-landing__secondary-btn"
          onClick={handlePrimaryEntry}
        >
          Generate Now
        </button>
        <div className="home-landing__cta-media">
          <img src={whaleImage} alt="Whale-themed SOALIVE landing artwork" />
        </div>
      </section>
      <footer className="home-landing__footer">
        <div className="home-landing__footer-copy">
          <div className="home-landing__footer-brand">{renderLandingBrand()}</div>
          <p>Let&apos;s Build Your Next Visual Story.</p>
        </div>
        <small>
          Copyright © 2026 <span className="home-landing__footer-copy-brand">{renderLandingBrand()}</span>
        </small>
        <nav className="home-landing__footer-links" aria-label="Legal documents">
          <a href="/terms-of-use">Terms of Use</a>
          <a href="/privacy-policy">Privacy Policy</a>
        </nav>
      </footer>
    </div>
  );
};

// Retained for reference while the member homepage uses the landing layout.
// eslint-disable-next-line no-unused-vars
const DashboardHome = ({ history, user }) => {
  const routerHistory = history || { push: () => {} };
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState(null);
  const [collections, setCollections] = useState(() =>
    sortCollections(mapCollections(getCollectionsForUser(CURRENT_USER_ID))),
  );
  const [subscription, setSubscriptionState] = useState(() => {
    const existing = getSubscription(CURRENT_USER_ID);
    if (existing) {
      return existing;
    }
    if (FALLBACK_SUBSCRIPTION) {
      return ensureSubscription(CURRENT_USER_ID, FALLBACK_SUBSCRIPTION);
    }
    return null;
  });
  const [pendingPlanRequest, setPendingPlanRequest] = useState(() =>
    getPendingRequestForUser(CURRENT_USER_ID),
  );
  const [publishedPlans, setPublishedPlans] = useState(() => {
    const plans = getPublishedPlans();
    if (Array.isArray(plans) && plans.length > 0) {
      return plans;
    }
    return FALLBACK_PUBLISHED_PLANS;
  });
  const [profile, setProfile] = useState(() => mapUserToProfile(user));
  const [accountSubscription, setAccountSubscription] = useState(null);
  const [isAccountSubscriptionLoading, setIsAccountSubscriptionLoading] = useState(true);

  useEffect(() => {
    setProfile((prev) => ({
      ...prev,
      ...mapUserToProfile(user),
    }));
  }, [user]);

  useEffect(() => {
    let isMounted = true;

    async function loadAccountSummary() {
      if (!user || !user.id) {
        if (isMounted) {
          setAccountSubscription(null);
          setIsAccountSubscriptionLoading(false);
        }
        return;
      }

      setIsAccountSubscriptionLoading(true);
      try {
        const response = await accountApi.fetchProfile();
        if (!isMounted) {
          return;
        }
        const payload = response && response.data ? response.data : {};
        if (payload.user) {
          setProfile((prev) => ({
            ...prev,
            ...mapUserToProfile(payload.user),
          }));
        }
        setAccountSubscription(
          payload.subscription ? mapSubscriptionFromApi(payload.subscription) : null,
        );
      } catch (error) {
        if (isMounted) {
          setAccountSubscription(null);
        }
      } finally {
        if (isMounted) {
          setIsAccountSubscriptionLoading(false);
        }
      }
    }

    loadAccountSummary();

    return () => {
      isMounted = false;
    };
  }, [user && user.id]);

  useEffect(() => {
    let mounted = true;
    const fetchHistory = async () => {
      setHistoryLoading(true);
      setHistoryError(null);
      try {
        const response = await aiApi.fetchHistory({ limit: MAX_HISTORY_ITEMS });
        const responseData = response && response.data ? response.data : {};
        const items = safeArray(responseData.items)
          .map(normalizeHistoryEntry)
          .filter(Boolean);
        if (!mounted) {
          return;
        }
        setHistoryItems(items);
      } catch (error) {
        if (!mounted) {
          return;
        }
        setHistoryError(error);
        setHistoryItems(FALLBACK_HISTORY);
      } finally {
        if (mounted) {
          setHistoryLoading(false);
        }
      }
    };

    fetchHistory();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const handleCollectionChange = (allCollections) => {
      const userCollections = safeArray(allCollections).filter(
        (collection) => collection && collection.ownerId === CURRENT_USER_ID,
      );
      setCollections(sortCollections(mapCollections(userCollections)));
    };

    handleCollectionChange(getCollectionsForUser(CURRENT_USER_ID));
    const unsubscribe = subscribeToCollectionChanges(handleCollectionChange);
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  const upgradeLegacySubscription = useCallback(
    (current) => {
      if (!current) {
        return current;
      }
      const rawPlanId = String(current.planId || '').toLowerCase();
      const rawPlanName = String(current.planName || '').toLowerCase();
      const mappedPlanId = LEGACY_PLAN_ID_MAP[rawPlanId] || LEGACY_PLAN_ID_MAP[rawPlanName];
      if (!mappedPlanId) {
        return current;
      }

      const planFromPublished =
        Array.isArray(publishedPlans) &&
        publishedPlans.find(
          (plan) =>
            plan &&
            (plan.planId === mappedPlanId ||
              plan.productId === mappedPlanId ||
              plan.id === mappedPlanId),
        );
      const planFromCatalog = PLAN_CATALOG.find((plan) => plan.id === mappedPlanId);
      const plan = planFromPublished || planFromCatalog;
      if (!plan) {
        return current;
      }

      const planPriceAmount =
        plan.priceAmount !== undefined
          ? plan.priceAmount
          : plan.planPriceAmount !== undefined
          ? plan.planPriceAmount
          : current.planPriceAmount || current.priceAmount || 0;
      const planBillingPeriod =
        plan.billingPeriod !== undefined
          ? plan.billingPeriod
          : plan.planBillingPeriod !== undefined
          ? plan.planBillingPeriod
          : current.planBillingPeriod || 1;
      const planBillingUnit =
        plan.billingUnit ||
        plan.planBillingUnit ||
        current.planBillingUnit ||
        current.billingUnit ||
        'month';

      const normalizedRecord = {
        ...current,
        planId: mappedPlanId,
        planName: plan.name || current.planName,
        planFeatures: Array.isArray(plan.features)
          ? plan.features
          : Array.isArray(plan.planFeatures)
          ? plan.planFeatures
          : [],
        planPriceAmount: planPriceAmount,
        planBillingPeriod: planBillingPeriod,
        planBillingUnit: planBillingUnit,
        planPriceDisplay: formatMonthlyPrice(planPriceAmount, planBillingPeriod, planBillingUnit),
      };

      const stored = persistSubscription(CURRENT_USER_ID, normalizedRecord);
      return stored || normalizedRecord;
    },
    [publishedPlans],
  );

  useEffect(() => {
    const syncSubscription = () => {
      const existing = getSubscription(CURRENT_USER_ID);
      const normalizedExisting = upgradeLegacySubscription(existing);
      if (normalizedExisting) {
        setSubscriptionState(normalizedExisting);
        return;
      }
      if (FALLBACK_SUBSCRIPTION) {
        const ensured = ensureSubscription(CURRENT_USER_ID, FALLBACK_SUBSCRIPTION);
        const normalized = upgradeLegacySubscription(ensured);
        setSubscriptionState(normalized || ensured || null);
        return;
      }
      setSubscriptionState(null);
    };

    syncSubscription();
    const unsubscribe = subscribeToSubscriptionChanges((map) => {
      const entry = map && map[CURRENT_USER_ID];
      if (entry) {
        const normalized = upgradeLegacySubscription(entry);
        setSubscriptionState(normalized || entry);
        return;
      }
      syncSubscription();
    });
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [upgradeLegacySubscription]);

  useEffect(() => {
    const syncPendingRequest = () => {
      setPendingPlanRequest(getPendingRequestForUser(CURRENT_USER_ID));
    };

    syncPendingRequest();
    const unsubscribe = subscribeToRequestChanges((requests) => {
      if (!Array.isArray(requests)) {
        syncPendingRequest();
        return;
      }
      const pending = requests.find(
        (request) => request && request.userId === CURRENT_USER_ID && request.status === 'pending',
      );
      setPendingPlanRequest(pending || null);
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);
  useEffect(() => {
    const unsubscribe = subscribeToPublishedPlans((plans) => {
      if (Array.isArray(plans) && plans.length > 0) {
        setPublishedPlans(plans);
      } else {
        setPublishedPlans(FALLBACK_PUBLISHED_PLANS);
      }
    });
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  const recommendedPlan = useMemo(() => selectRecommendedPlan(publishedPlans), [publishedPlans]);
  const legacyApprovedSubscription =
    subscription && subscription.status && subscription.status !== 'recommended'
      ? subscription
      : null;
  const activeSubscription = accountSubscription || legacyApprovedSubscription;
  const displaySubscription = activeSubscription || recommendedPlan;

  const planDetails = useMemo(() => {
    if (!displaySubscription) {
      return null;
    }
    const planId = displaySubscription.planId;
    const matchedPublishedPlan =
      planId && Array.isArray(publishedPlans)
        ? publishedPlans.find(
            (plan) => plan && (plan.planId === planId || plan.productId === planId),
          )
        : null;
    const catalogPlan = planId ? getPlanById(planId) : null;
    const planSource = matchedPublishedPlan || catalogPlan;

    const name =
      displaySubscription.planName ||
      (matchedPublishedPlan && matchedPublishedPlan.name) ||
      (catalogPlan && catalogPlan.name) ||
      planId ||
      'Custom plan';

    const billing = resolveMonthlyPriceLabel({ plan: planSource, subscription: displaySubscription });

    const features =
      safeArray(displaySubscription.planFeatures).length > 0
        ? safeArray(displaySubscription.planFeatures)
        : safeArray(planSource && planSource.features);

    const renewal = formatRenewalLabel(
      (planSource && planSource.billingPeriod) || displaySubscription.planBillingPeriod,
      (planSource && planSource.billingUnit) || displaySubscription.planBillingUnit,
    );

    const rawStatus = displaySubscription.status || (activeSubscription ? 'approved' : 'recommended');
    const statusKey = String(rawStatus).toLowerCase();
    const statusLabel = activeSubscription
      ? SUBSCRIPTION_STATUS_LABELS[statusKey] || 'Active'
      : 'Recommended plan';

    return {
      id: planId || (planSource && planSource.planId) || 'custom',
      name,
      billing,
      features,
      renewal,
      statusLabel,
      statusKey: activeSubscription ? statusKey : 'neutral',
      isRecommended: !activeSubscription,
    };
  }, [activeSubscription, displaySubscription, publishedPlans]);

  const workspaceStats = useMemo(() => {
    const totalCollections = collections.length;
    const totalArtworks = collections.reduce(
      (accumulator, collection) => accumulator + (collection.artworkCount || 0),
      0,
    );
    const latestGeneration = historyItems[0] || null;
    const planStatLabel = planDetails
      ? planDetails.isRecommended
        ? 'Recommended plan'
        : `${planDetails.name} plan`
      : 'Credit Plan';
    const planStatValue = planDetails ? planDetails.billing : 'Set up billing';
    const planStatCaption = pendingPlanRequest
      ? 'Upgrade request pending approval'
      : planDetails && !planDetails.isRecommended
      ? 'Manage seats & billing'
      : 'Review plans and activate your workspace';

    return [
      {
        id: 'workspace-collections',
        label: 'Collections',
        value: totalCollections,
        caption:
          totalCollections > 0
            ? `${totalArtworks} artwork${totalArtworks === 1 ? '' : 's'} organized`
            : 'Create your first collection',
        onClick: () => routerHistory.push('/templates'),
      },
      {
        id: 'workspace-generator',
        label: 'Latest generation',
        value: latestGeneration ? formatRelativeTime(latestGeneration.createdAt) : '—',
        caption: latestGeneration
          ? truncate(latestGeneration.prompt, 68)
          : 'Head to the AI studio to generate visuals.',
        onClick: () => routerHistory.push('/ai'),
      },
      {
        id: 'workspace-plan',
        label: planStatLabel,
        value: planStatValue,
        caption: planStatCaption,
        onClick: () => routerHistory.push('/account'),
      },
    ];
  }, [collections, historyItems, pendingPlanRequest, planDetails, routerHistory]);

  const shortcuts = useMemo(
    () => [
      {
        id: 'shortcut-generate',
        title: 'Create with AI',
        description: 'Launch the studio to craft new concepts, variations, and brand-ready assets.',
        action: () => routerHistory.push('/ai'),
        actionLabel: 'Open studio',
      },
      {
        id: 'shortcut-catalog',
        title: 'Organize templates',
        description: 'Curate collections, add collaborators, and keep inspiration centralized.',
        action: () => routerHistory.push('/templates'),
        actionLabel: 'Browse templates',
      },
      {
        id: 'shortcut-history',
        title: 'Review gallery',
        description: 'Browse generated images, open details, and reuse previous outputs.',
        action: () => routerHistory.push('/gallery'),
        actionLabel: 'View gallery',
      },
      {
        id: 'shortcut-account',
        title: 'Tune preferences',
        description: 'Update your creative profile, workspace defaults, and notification settings.',
        action: () => routerHistory.push('/account'),
        actionLabel: 'Manage account',
      },
    ],
    [routerHistory],
  );

  const collectionsToShow = collections.slice(0, 3);
  const showFallbackHistory = historyError && historyItems.length > 0;

  const planLoading =
    isAccountSubscriptionLoading && !activeSubscription && !recommendedPlan;
  const greetingName =
    profile.displayName ||
    profile.email ||
    (activeSubscription && activeSubscription.userName) ||
    'Member';

  return (
    <section className="home-section">
      <header className="home-hero" aria-label="Workspace overview">
        <div className="home-hero__content">
          <p className="home-hero__eyebrow">Welcome back, {greetingName}</p>
          <h1>Let&rsquo;s Build Your Next Visual Story</h1>
          <p className="home-hero__description">
            Generate campaign-ready imagery, organize winning concepts, and keep your team aligned —
            all from one creative command center.
          </p>
          <div className="home-hero__actions">
            <button
              type="button"
              className="home-button home-button--primary"
              onClick={() => routerHistory.push('/ai')}
            >
              Generate images
            </button>
            <button
              type="button"
              className="home-button home-button--ghost"
              onClick={() => routerHistory.push('/templates')}
            >
              Open templates
            </button>
          </div>
          {historyItems.length > 0 && (
            <dl className="home-hero__insights">
              <div className="home-hero__insight">
                <dt>Latest prompt</dt>
                <dd>{truncate(historyItems[0].prompt, 96)}</dd>
              </div>
              <div className="home-hero__insight">
                <dt>Last generated</dt>
                <dd>{formatRelativeTime(historyItems[0].createdAt)}</dd>
              </div>
            </dl>
          )}
        </div>
        <aside className="home-plan-card" aria-label="Credit Plan summary">
          <div className="home-plan-card__header">
            <span className="home-plan-card__eyebrow">
              {planDetails && planDetails.isRecommended ? 'Recommended plan' : 'Your plan'}
            </span>
            <h2>
              {planDetails
                ? planDetails.name
                : planLoading
                ? 'Loading credit plan...'
                : 'No active plan'}
            </h2>
            <p className="home-plan-card__price">
              {planDetails
                ? planDetails.billing
                : planLoading
                ? 'Checking billing'
                : 'Set up billing'}
            </p>
            {planDetails && planDetails.renewal && (
              <p className="home-plan-card__renewal">{planDetails.renewal}</p>
            )}
            {(planDetails && planDetails.statusLabel) || pendingPlanRequest ? (
              <div className="home-plan-card__status-row">
                {planDetails && planDetails.statusLabel && (
                  <span
                    className={`home-plan-card__status home-plan-card__status--${planDetails.statusKey}`}
                  >
                    {planDetails.statusLabel}
                  </span>
                )}
                {pendingPlanRequest && (
                  <span className="home-plan-card__badge">Upgrade request pending</span>
                )}
              </div>
            ) : null}
          </div>
          {planDetails && planDetails.features.length > 0 && (
            <ul className="home-plan-card__features">
              {planDetails.features.slice(0, 4).map((feature, index) => (
                <li key={`${planDetails.id}-feature-${index}`}>{feature}</li>
              ))}
            </ul>
          )}
          <div className="home-plan-card__actions">
            <button
              type="button"
              className="home-button home-button--secondary"
              onClick={() => routerHistory.push('/account')}
            >
              {planDetails && planDetails.isRecommended ? 'View plans' : 'Manage Credit Plan'}
            </button>
          </div>
        </aside>
      </header>

      <section className="home-stats" aria-label="Workspace stats">
        {workspaceStats.map((stat) => (
          <button
            key={stat.id}
            type="button"
            className="home-stat-card"
            onClick={stat.onClick}
          >
            <span className="home-stat-card__label">{stat.label}</span>
            <span className="home-stat-card__value">{stat.value}</span>
            <span className="home-stat-card__caption">{stat.caption}</span>
          </button>
        ))}
      </section>

      <div className="home-grid">
        <section className="home-panel" aria-labelledby="home-history-heading">
          <div className="home-panel__header">
            <div>
              <h2 id="home-history-heading">Recent Generations</h2>
              <p className="home-panel__subtitle">
                Revisit your freshest explorations and continue iterating with the AI studio.
              </p>
            </div>
            <button
              type="button"
              className="home-link"
              onClick={() => routerHistory.push('/gallery')}
            >
              View gallery
            </button>
          </div>

          {historyLoading && historyItems.length === 0 ? (
            <div className="home-blank-state">
              <p>Fetching your latest generations&hellip;</p>
            </div>
          ) : historyItems.length === 0 ? (
            <div className="home-blank-state">
              <p>No generations yet.</p>
              <button
                type="button"
                className="home-button home-button--primary"
                onClick={() => routerHistory.push('/ai')}
              >
                Start generating
              </button>
            </div>
          ) : (
            <>
              {showFallbackHistory && (
                <p className="home-panel__notice">
                  Unable to refresh history — showing sample data for context.
                </p>
              )}
              <ul className="home-history-grid">
                {historyItems.slice(0, 4).map((item) => {
                  const rawStatus = item.status || 'completed';
                  const statusKey = String(rawStatus)
                    .toLowerCase()
                    .replace(/[^a-z0-9-]/g, '');
                  const statusLabel = String(rawStatus).replace(/[_-]/g, ' ');
                  const statusText =
                    statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1);
                  return (
                    <li key={item.id}>
                      <article className="home-history-card">
                        <figure className="home-history-card__preview">
                          <img src={item.imageUrl} alt="" loading="lazy" />
                        </figure>
                        <div className="home-history-card__body">
                          <header className="home-history-card__meta">
                            <span
                              className={`home-history-card__status home-history-card__status--${statusKey}`}
                            >
                              {statusText}
                            </span>
                            <time dateTime={item.createdAt}>{formatRelativeTime(item.createdAt)}</time>
                          </header>
                          <p className="home-history-card__prompt">{item.prompt}</p>
                        </div>
                        <footer className="home-history-card__footer">
                          <button
                            type="button"
                            className="home-link"
                            onClick={() => routerHistory.push('/ai')}
                          >
                            Open in studio
                          </button>
                        </footer>
                      </article>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </section>

        <section className="home-panel" aria-labelledby="home-collections-heading">
          <div className="home-panel__header">
            <div>
              <h2 id="home-collections-heading">Collections Spotlight</h2>
              <p className="home-panel__subtitle">
                Keep your strongest sets within reach and ready for stakeholder reviews.
              </p>
            </div>
            <button
              type="button"
              className="home-link"
              onClick={() => routerHistory.push('/templates')}
            >
              View templates
            </button>
          </div>

          {collectionsToShow.length === 0 ? (
            <div className="home-blank-state">
              <p>You haven&rsquo;t curated any collections yet.</p>
              <button
                type="button"
                className="home-button home-button--secondary"
                onClick={() => routerHistory.push('/templates')}
              >
                Curate a collection
              </button>
            </div>
          ) : (
            <ul className="home-collection-grid">
              {collectionsToShow.map((collection) => (
                <li key={collection.id}>
                  <button
                    type="button"
                    className="home-collection-card"
                    onClick={() =>
                      routerHistory.push({
                        pathname: `/templates/collections/${collection.id}`,
                        state: { collection },
                      })
                    }
                  >
                    <figure className="home-collection-card__media">
                      <img src={collection.coverImage} alt="" loading="lazy" />
                    </figure>
                    <div className="home-collection-card__body">
                      <header className="home-collection-card__header">
                        <div>
                          <h3>{collection.title}</h3>
                          <p>
                            {collection.description
                              ? truncate(collection.description, 96)
                              : collection.curator}
                          </p>
                        </div>
                        <span className="home-collection-card__date">
                          {formatDateShort(collection.updatedAt)}
                        </span>
                      </header>
                      <div className="home-collection-card__meta">
                        <span>
                          {collection.artworkCount} artwork
                          {collection.artworkCount === 1 ? '' : 's'}
                        </span>
                        <span>{collection.curator}</span>
                      </div>
                      {collection.tags.length > 0 && (
                        <ul className="home-collection-card__tags">
                          {collection.tags.map((tag) => (
                            <li key={`${collection.id}-${tag}`}>{tag}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="home-panel" aria-labelledby="home-shortcuts-heading">
        <div className="home-panel__header">
          <div>
            <h2 id="home-shortcuts-heading">Workspace Shortcuts</h2>
            <p className="home-panel__subtitle">
              Jump into the workflows your team uses most and move projects forward faster.
            </p>
          </div>
        </div>
        <ul className="home-shortcuts">
          {shortcuts.map((shortcut) => (
            <li key={shortcut.id} className="home-shortcut-card">
              <div className="home-shortcut-card__body">
                <h3>{shortcut.title}</h3>
                <p>{shortcut.description}</p>
              </div>
              <button
                type="button"
                className="home-link"
                onClick={shortcut.action}
              >
                {shortcut.actionLabel}
              </button>
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
};

const Home = ({ history, user, openAuthView }) => {
  return <LandingHome history={history} openAuthView={openAuthView} user={user} />;
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
});

const mapDispatchToProps = {
  openAuthView,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Home));
